import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Check, X, Zap } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { RoleBadge } from '../primitives/Badge';
import { AssigneePicker } from './AssigneePicker';
import { timeFromMin } from '../../lib/helpers';
import { supabase } from '../../lib/supabase';
import { deferTask } from '../../lib/taskActions';

export const TaskPopover = ({ task, block, anchorRect, onClose, onUpdate }) => {
  const [title, setTitle] = useState(task.title);
  const [priority, setPriority] = useState(task.priority || 2);
  const [effort, setEffort] = useState(task.effort || '1h');
  const [dueLabel, setDueLabel] = useState(task.due_label || '');
  const [assigneeId, setAssigneeId] = useState(task.assignee_id || null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const role = task.roles || task.goals?.roles;
  const r = role?.slug ? ROLES[role.slug] : null;
  const goalTitle = task.goals?.title;

  const save = async () => {
    setSaving(true);
    await supabase.from('tasks').update({
      title, priority, effort, due_label: dueLabel || null,
    }).eq('id', task.id);
    setSaving(false);
    setDirty(false);
    onUpdate();
  };

  const toggleDone = async () => {
    const newDone = !task.done;
    await supabase.from('tasks').update({ done: newDone, done_at: newDone ? new Date().toISOString() : null }).eq('id', task.id);
    onUpdate();
    onClose();
  };

  const changePriority = async (p) => {
    setPriority(p);
    setDirty(true);
    await supabase.from('tasks').update({ priority: p }).eq('id', task.id);
    onUpdate();
  };

  const changeAssignee = async (id) => {
    setAssigneeId(id);
    await supabase.from('tasks').update({ assignee_id: id }).eq('id', task.id);
    onUpdate();
  };

  const doDefer = async (when) => {
    await deferTask(task, when);
    onUpdate();
    onClose();
  };

  const inputStyle = {
    width: '100%', padding: '6px 10px', borderRadius: 8, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
    fontFamily: 'inherit', color: T.text, outline: 'none',
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={springs.snappy}
        style={{
          position: 'absolute', zIndex: 50,
          top: anchorRect.top, left: anchorRect.left,
          width: 320,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.9)',
          borderRadius: 14, boxShadow: T.shadowLg,
        }}
      >
        {/* Header */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <button onClick={toggleDone} style={{
              width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
              border: `1.5px solid ${task.done ? T.green : 'rgba(0,0,0,0.15)'}`,
              background: task.done ? T.greenLight : 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {task.done && <Check size={12} color={T.green} />}
            </button>
            <input value={title} onChange={e => { setTitle(e.target.value); setDirty(true); }}
              style={{
                ...inputStyle, border: 'none', padding: 0, fontSize: 14, fontWeight: 600,
                background: 'transparent', flex: 1,
              }}
            />
            <button onClick={onClose} style={{
              width: 24, height: 24, borderRadius: 6, border: 'none',
              background: 'rgba(0,0,0,0.04)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <X size={13} color={T.textMuted} />
            </button>
          </div>
          {goalTitle && (
            <div style={{ marginTop: 6, marginLeft: 30, fontSize: 11, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Target size={10} /> {goalTitle}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Priority */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Priority</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { val: 1, label: 'Urgent', color: T.red, bg: T.redLight, border: 'rgba(224,88,88,0.25)' },
                { val: 2, label: 'This week', color: T.amber, bg: T.amberLight, border: T.amberBorder },
                { val: 3, label: 'Later', color: T.textMuted, bg: 'rgba(0,0,0,0.04)', border: 'rgba(0,0,0,0.08)' },
              ].map(p => (
                <button key={p.val} onClick={() => changePriority(p.val)} style={{
                  flex: 1, padding: '6px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  background: priority === p.val ? p.bg : 'rgba(0,0,0,0.02)',
                  color: priority === p.val ? p.color : T.textLight,
                  border: `1.5px solid ${priority === p.val ? p.border : 'rgba(0,0,0,0.06)'}`,
                }}>{p.label}</button>
              ))}
            </div>
          </div>

          {/* Defer */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Defer</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[{ when: 'tomorrow', label: 'Tomorrow' }, { when: 'nextweek', label: 'Next week' }].map(d => (
                <button key={d.when} onClick={() => doDefer(d.when)} style={{
                  flex: 1, padding: '6px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: 'rgba(0,0,0,0.02)', color: T.textMid, border: '1.5px solid rgba(0,0,0,0.06)',
                }}>{d.label}</button>
              ))}
              <input type="date"
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                onChange={e => { if (e.target.value) doDefer(new Date(e.target.value + 'T09:00:00')); }}
                title="Defer to a date"
                style={{
                  flex: 1, padding: '5px 6px', borderRadius: 8, fontSize: 11,
                  border: '1.5px solid rgba(0,0,0,0.06)', background: 'rgba(0,0,0,0.02)',
                  color: T.textMid, fontFamily: 'inherit',
                }}
              />
            </div>
          </div>

          {/* Assignee */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Assignee</div>
            <AssigneePicker value={assigneeId} current={task.assignee} onChange={changeAssignee} />
          </div>

          {/* Effort + Due */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Effort</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {['30m', '1h', '2h', '4h'].map(v => (
                  <button key={v} onClick={() => { setEffort(v); setDirty(true); }} style={{
                    flex: 1, padding: '5px 4px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                    cursor: 'pointer', fontFamily: 'inherit',
                    background: effort === v ? T.amberLight : 'rgba(0,0,0,0.02)',
                    color: effort === v ? T.amber : T.textMuted,
                    border: `1px solid ${effort === v ? T.amberBorder : 'rgba(0,0,0,0.06)'}`,
                  }}>{v}</button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Due</div>
              <input value={dueLabel} onChange={e => { setDueLabel(e.target.value); setDirty(true); }}
                placeholder="e.g. Today, Tomorrow"
                style={{ ...inputStyle, fontSize: 12, padding: '5px 8px' }}
              />
            </div>
          </div>

          {/* Role + Time */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {r && <RoleBadge roleSlug={role.slug} small />}
            {block && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted,
                fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontFeatureSettings: '"tnum"',
              }}>
                {timeFromMin(block.startMin)} – {timeFromMin(block.endMin)}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        {dirty && (
          <div style={{
            padding: '10px 16px', borderTop: '1px solid rgba(0,0,0,0.05)',
            display: 'flex', justifyContent: 'flex-end',
          }}>
            <button onClick={save} disabled={saving} style={{
              padding: '6px 16px', borderRadius: 8, border: 'none',
              background: T.amber, color: '#fff', cursor: 'pointer', fontSize: 12,
              fontWeight: 600, fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
            }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </motion.div>
    </>
  );
};
